/**
 * `/:world/series/:slug`: one sub-series, then its books in reading order,
 * drawn as a path.
 *
 * Each book carries its note — one sentence on why it sits where it does. That
 * sentence is the point of the page, so it is shown in full, not as a footnote.
 * Availability is not repeated here: on a list it is clutter, on the book's
 * own page it is information, and the book's page has it.
 */

import { Link, useParams } from 'react-router'
import { worldPath } from '../../shared/routes.ts'
import { CataloguePage, SeriesCards, usePageTitle, useStops } from '../components/Catalogue.tsx'
import { Compass, Knot } from '../components/Ornament.tsx'
import { Path } from '../components/Path.tsx'
import { ReportError } from '../components/ReportError.tsx'
import { useI18n } from '../i18n/index.tsx'
import { useShelf } from '../lib/shelf.tsx'
import {
  fill,
  mainTitle,
  note,
  seriesDescription,
  seriesName,
  workBySlug,
  type Loaded,
} from '../lib/catalogue.ts'

/**
 * How many of the series' books the signed-in reader has marked read. Nothing
 * for a reader who is signed out: the page never asks for an account.
 */
function Progress({ books }: { books: string[] }) {
  const { t } = useI18n()
  const { state } = useShelf()
  if (state.status !== 'ready') return null
  const read = books.filter((work) => state.entries.get(work)?.status === 'read').length
  return <p className="series-head__progress">{fill(t('series.progress'), { read, count: books.length })}</p>
}

export function SeriesDetail() {
  return <CataloguePage>{(catalogue, world) => <SeriesView catalogue={catalogue} world={world} />}</CataloguePage>
}

function SeriesView({ catalogue, world }: { catalogue: Loaded; world: string }) {
  const { t, locale } = useI18n()
  const stopsLabel = useStops()
  const { slug } = useParams()
  const series = catalogue.series.find((s) => s.slug === slug)
  usePageTitle(series === undefined ? t('series.not_found.title') : seriesName(series, locale))

  if (series === undefined) {
    return (
      <>
        <section className="panel wide">
          <h1>{t('series.not_found.title')}</h1>
          <p>{t('series.not_found.body')}</p>
        </section>
        <SeriesCards catalogue={catalogue} world={world} headingLevel={2} />
      </>
    )
  }

  const description = seriesDescription(series, locale)
  const stops = series.books.flatMap((book, index) => {
    const work = workBySlug(catalogue, book.work)
    return work === undefined
      ? []
      : [
          {
            position: index + 1,
            title: mainTitle(work, locale),
            href: worldPath(world, 'books', work.slug),
            year: work.year_first_published,
            note: note(book.note_bg, locale),
          },
        ]
  })

  return (
    <>
      <section className="series-head wide">
        <div className="series-head__text">
          <p className="label series-head__kicker">{fill(t('series.kicker'), { stops: stopsLabel(stops.length) })}</p>
          <h1>{seriesName(series, locale)}</h1>
          {description === null ? null : <p>{description}</p>}
          <Progress books={series.books.map((book) => book.work)} />
        </div>
        <div className="series-head__mark">
          <Compass size={96} on="paper" />
        </div>
        <Knot />
      </section>

      <section className="path-panel wide">
        <h2 className="label">{t('series.reading_order')}</h2>
        <Path stops={stops} label={t('series.reading_order')} />
      </section>

      <nav className="panel__links wide">
        <Link to={worldPath(world, 'series')}>{t('series.all')}</Link>
      </nav>
      <div className="wide">
        <ReportError />
      </div>
    </>
  )
}
